import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

async function api(path, options = {}) {
  const res = await fetch(`${API}/api${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(fmtErr(data.detail));
  return data;
}

function fmtErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Something went wrong. Please try again.";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = authed
  const [ready, setReady] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const u = await api("/auth/me");
      setUser(u);
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz && tz !== u.timezone && !sessionStorage.getItem("am_tz_synced")) {
          sessionStorage.setItem("am_tz_synced", "1");
          api("/me/timezone", { method: "POST", body: JSON.stringify({ timezone: tz }) }).catch(() => {});
        }
      } catch {}
    } catch (e) {
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    // Returning from Google OAuth: AuthCallback handles the exchange first.
    if (window.location.hash?.includes("session_id=")) {
      setReady(true);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const u = await api("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
    setUser(u);
    return u;
  };

  const register = async (email, password, name) => {
    const u = await api("/auth/register", { method: "POST", body: JSON.stringify({ email, password, name }) });
    setUser(u);
    return u;
  };

  const completeGoogle = async (sessionId) => {
    const u = await api("/auth/session", { method: "POST", body: JSON.stringify({ session_id: sessionId }) });
    setUser(u);
    return u;
  };

  const forgotPassword = (email) =>
    api("/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) });

  const resetPassword = (token, password) =>
    api("/auth/reset-password", { method: "POST", body: JSON.stringify({ token, password }) });

  const verifyEmail = (token) =>
    api("/auth/verify-email", { method: "POST", body: JSON.stringify({ token }) });

  const resendVerification = () =>
    api("/auth/resend-verification", { method: "POST" });

  const recoverEmail = (email) =>
    api("/auth/recover-email", { method: "POST", body: JSON.stringify({ email }) });

  const verifyRecovery = (token) =>
    api("/auth/verify-recovery", { method: "POST", body: JSON.stringify({ token }) });

  const changeEmail = (email) =>
    api("/auth/change-email", { method: "POST", body: JSON.stringify({ email }) });

  const cancelEmailChange = () =>
    api("/auth/cancel-email-change", { method: "POST" });

  const setPassword = (new_password, current_password) =>
    api("/auth/set-password", { method: "POST", body: JSON.stringify({ new_password, current_password }) });

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/home";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api("/auth/logout", { method: "POST" }); } catch (e) {}
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, ready, login, register, completeGoogle, loginWithGoogle, logout, checkAuth, forgotPassword, resetPassword, verifyEmail, resendVerification, recoverEmail, verifyRecovery, changeEmail, cancelEmailChange, setPassword }}>
      {children}
    </AuthCtx.Provider>
  );
}
